import {
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonTitle,
} from '@ionic/react';
import {
  ellipsisVertical,
  informationCircleOutline,
  timeOutline,
} from 'ionicons/icons';
import { cloneDeep, isEmpty } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useHasPermission } from '../../../../api/authorization.provider';
import {
  Scratchpad,
  ScratchpadBucketType,
} from '../../../../api/graphql/generated';
import { useHasFeature } from '../../../../api/hooks/feature.hooks';
import { useTeamRoleContext } from '../../../../api/hooks/role.hooks';
import { useCurrentUserProp } from '../../../../api/user.hooks';
import {
  ActionButton,
  ActionButtonRef,
} from '../../../../components/button/action-button';
import { BackButtonProvider } from '../../../../components/header/back-button';
import { NestedPageHeader } from '../../../../components/header/page.header';
import { welderKeyOn } from '../../../../components/icons/welder-key-on';
import { MachineProgramStatus } from '../../../../components/machine/machine-program-status';
import { ProgramState } from '../../../../components/machine/machine-state';
import { ScratchpadPlaylistOptions } from '../../../../components/machine/playlist/scratchpad-playlist-options';
import { ScratchpadHistoryModal } from '../../../../components/scratchpad/scratchpad-history-modal';
import i18n from '../../../../i18n';
import { getLocalizedText } from '../../../../i18n/i18n.utils';
import {
  useIsMachineActive,
  useMachineContext,
} from '../../../../pages/machine/machine-context-provider';
import { useBeaconRouter } from '../../../../route/route.hooks';
import { DefaultClipboardService } from '../../../../service/ClipboardService';
import { logger } from '../../../../util/logger';
import { usePreventScreenLock } from '../../../../util/screen-lock.hooks';
import { ScratchpadPageProps } from '../../../app-slugs';
import { FlowActionBar } from '../../shared/component/action/flow-action-bar';
import { FlowComponent } from '../../shared/component/flow/flow';
import {
  FlowQuickAction,
  FlowRefContext,
  useCreateFlowRef,
} from '../../shared/component/flow/flow-ref';
import {
  ApplicationSettingsModalProvider,
  ApplicationSettingsModalRef,
} from '../../shared/component/settings/application-settings-modal-provider';
import { ClipboardContextProvider } from '../../shared/hook/clipboard.hooks';
import {
  FlowServiceContextFactoryCallback,
  FlowServiceContextFactoryOptions,
  FlowServiceContextFactoryResult,
  FlowServiceContextProvider,
  SaveFlowOptions,
  useFlowServices,
} from '../../shared/hook/flow-service.hooks';
import { WaypointCaptureContextProvider } from '../../shared/hook/waypoint-capture.hooks';
import {
  Flow,
  FlowItem,
  FlowValidateError,
  FlowValidateResponse,
} from '../../shared/model/flow.types';
import { FlowAnalyticService } from '../../shared/service/flow-analytic.service';
import { FlowRuleServiceImpl } from '../../shared/service/flow-rule.service';
import {
  instanceOfFlowItemSection,
  instanceOfFlowItemSectionParent,
} from '../../shared/util/flow-item.util';
import { useHasBeaconWeldingFieldPermission } from '../../shared/util/permission';
import { WeldingEditActions } from './component/action/welding-edit-actions';
import { WeldingFlowSections } from './component/flow/welding-flow-sections';
import { useMultipassPath } from './component/flow-item/multipass/flow-multipass-path';
import { useStitchPath } from './component/flow-item/stitch/flow-stitch-path';
import { WeldingFlowItemComponent } from './component/flow-item/welding-flow-item';
import { WeldingFlowItemEdit } from './component/flow-item/welding-flow-item-edit';
import { WeldingFlowItemMultiEdit } from './component/flow-item/welding-flow-item-multi-edit';
import { multipassWeld } from './component/icon/multipass-weld';
import { stitchWeld } from './component/icon/stitch-weld';
import { WeldPartDetail } from './component/part-detail/welding-part-detail';
import { WeldPartDetailModal } from './component/part-detail/welding-part-detail-modal';
import { WeldApplicationSettingsBaseFormTabbed } from './component/settings/weld-application-settings-base-form-tabbed';
import { WeldingTeachDetail } from './component/teach/welding-teach-detail';
import { useValidatePath } from './hooks/flow-validation.hooks';
import { BeaconWeldingData } from './model/beacon-welding-data';
import { WeldingFlowItemServiceImpl } from './service/welding-flow-item.service';
import { WeldingFlowServiceImpl } from './service/welding-flow.service';
import {
  buildFlowItemParameterAdjustmentMetadata,
  convertToBeaconWeldingDataWelder,
} from './util/beacon-welding.util';

export enum CobotWelderClipboardPaths {
  FLOW_ITEMS = 'flow_items',
}

const clipboardService = new DefaultClipboardService();

export interface FlowSaveResult {
  scratchpad: Scratchpad | undefined;
  errors: FlowValidateResponse;
}

interface BeaconWeldingPageTitleProps {
  title: string;
}

enum FlowModal {
  PART_DETAILS,
  PART_HISTORY,
  WELD_SETTINGS,
}

type GetParameterAdjustmentMetadata = (
  equipment?: BeaconWeldingData.Welder
) => BeaconWeldingData.AppFlowItemParameterRefAdjustmentMetadata | undefined;

const BeaconWeldingPage = (props: ScratchpadPageProps): ReactElement => {
  const { scratchpad } = props;
  const { machine } = useMachineContext();
  const { role } = useTeamRoleContext();
  const isAdmin = useCurrentUserProp<boolean>('admin');
  const getParameterAdjustmentMetadata: GetParameterAdjustmentMetadata =
    useCallback(
      (equipment) => {
        if (!isAdmin && role) {
          return buildFlowItemParameterAdjustmentMetadata({
            permissions: role.permissions ?? [],
            equipment,
          });
        }
      },
      [isAdmin, role]
    );

  const flowServiceContextFactory: FlowServiceContextFactoryCallback<
    BeaconWeldingData.FlowConfig,
    typeof BeaconWeldingData.FlowSectionType
  > = useCallback(
    (options: FlowServiceContextFactoryOptions) => {
      const { license, scratchpad } = options;

      const weldingLicense =
        license.licenseFormValues as BeaconWeldingData.License;
      const welder = convertToBeaconWeldingDataWelder(weldingLicense.welder);

      if (!welder) {
        logger.error('Welder not recognized in license.', {
          license,
        });
        throw new Error('Failed to build flow service context.');
      }
      const flowItemService = new WeldingFlowItemServiceImpl(
        BeaconWeldingData.FlowItemsByWelder[
          welder as keyof typeof BeaconWeldingData.FlowItemsByWelder
        ],
        welder,
        getParameterAdjustmentMetadata(welder)
      );

      const flowRuleService = new FlowRuleServiceImpl();
      const flowService = new WeldingFlowServiceImpl(
        license,
        flowItemService,
        flowRuleService,
        BeaconWeldingData.Tools,
        machine
      );

      const appData = scratchpad.appData as Flow;

      /**
       * TODO - remove when all clients are on shared code https://github.com/Hirebotics/beacon/pull/1388
       *
       * We need to support the old attribute (weldParameterSet) and apply it as a default.
       * We check the old weldParameterSet first since new clients always update
       * both attributes while the old clients only update the weldParameterSet.
       * If the old client saves, the defaultParameterSet will be obsolete. So,
       * here we prefer the weldParameterSet.
       */
      const defaultParameterSet = cloneDeep(
        appData?.weldParameterSet ?? appData?.defaultParameterSet
      );

      if (defaultParameterSet) {
        // Ensure baseValue is set for all parameters
        defaultParameterSet.data.parameters =
          defaultParameterSet.data.parameters.map((p) => {
            return {
              ...p,
              baseValue: p.baseValue ?? p.value,
            };
          });

        flowItemService.setApplicationDefaultParameterSet(
          BeaconWeldingData.FlowItemRef.WELD,
          defaultParameterSet
        );
      }

      const context: FlowServiceContextFactoryResult<
        BeaconWeldingData.FlowConfig,
        typeof BeaconWeldingData.FlowSectionType
      > = {
        appSlug: BeaconWeldingData.APP_SLUG,
        flowService,
        flowAnalyticService: new FlowAnalyticService(
          BeaconWeldingData.ANALYTICS_PREFIX
        ),
        flowItemService,
      };

      return context;
    },
    [machine, getParameterAdjustmentMetadata]
  );

  if (scratchpad?.id) {
    return (
      <ClipboardContextProvider clipboardService={clipboardService}>
        <FlowServiceContextProvider contextFactory={flowServiceContextFactory}>
          {({ flow }) => {
            const config = flow.config as BeaconWeldingData.FlowConfig;

            return (
              <WaypointCaptureContextProvider
                ioMapping={config.ioMapping}
                toolConfig={config.torch}
              >
                <BeaconWeldingPageExisting {...props} />
              </WaypointCaptureContextProvider>
            );
          }}
        </FlowServiceContextProvider>
      </ClipboardContextProvider>
    );
  } else {
    return (
      <ClipboardContextProvider clipboardService={clipboardService}>
        <FlowServiceContextProvider contextFactory={flowServiceContextFactory}>
          {({ flow }) => {
            const config = flow.config as BeaconWeldingData.FlowConfig;

            return (
              <WaypointCaptureContextProvider
                ioMapping={config.ioMapping}
                toolConfig={config.torch}
              >
                <BeaconWeldingPageNew {...props} />
              </WaypointCaptureContextProvider>
            );
          }}
        </FlowServiceContextProvider>
      </ClipboardContextProvider>
    );
  }
};

const BeaconWeldingPageNew = (props: ScratchpadPageProps) => {
  const { teamId, machineId } = props;
  const { builder } = useBeaconRouter();
  const {
    replace,
    location: { state },
  } = useHistory();

  const afterSave = (scratchpad: Scratchpad): void => {
    replace(
      builder.getMachineScratchpadRoute(teamId, machineId, scratchpad.id),
      state
    );
  };

  return (
    <BackButtonProvider>
      <IonPage>
        <NestedPageHeader
          title={i18n.t('common:scratchpad_create_part')}
          defaultBackRoute={builder.getMachineRoute(teamId, machineId)}
        />
        <WeldPartDetail afterSave={afterSave} />
      </IonPage>
    </BackButtonProvider>
  );
};

const BeaconWeldingPageExisting = (props: ScratchpadPageProps) => {
  const { teamId, machineId, scratchpad } = props;

  const [activeModal, setActiveModal] = useState<FlowModal | undefined>();

  const { builder } = useBeaconRouter();
  const pageRef = useRef<HTMLDivElement>();
  const actionButtonRef = useRef<ActionButtonRef>();

  const { flow, saveFlow, flowService, flowItemService } =
    useFlowServices<BeaconWeldingData.FlowConfig>();
  const isMachineActive = useIsMachineActive();

  const [flowErrors, setFlowErrors] = useState<
    FlowValidateError[] | undefined
  >();

  usePreventScreenLock();

  const canUpdateScratchpad = useHasPermission(
    'update',
    'scratchpad',
    scratchpad
  );

  const canUpdateAllBeaconWeldingFields = useHasBeaconWeldingFieldPermission(
    scratchpad?.machineId,
    '*'
  );

  const { openStitchPath, stitchPathComponent } = useStitchPath();
  const { openMultipassPath, multipassPathComponent } = useMultipassPath();

  const hasMultipassFeature = useHasFeature<boolean>(
    'beacon-welding-multipass'
  );

  const { machine } = useMachineContext();

  const onModalChange = (modal: FlowModal | undefined) => {
    setActiveModal(modal);
    actionButtonRef.current?.dismiss();
  };

  const save = useCallback(
    async (flow: Flow, options?: SaveFlowOptions): Promise<FlowSaveResult> => {
      const errors = await flowService.validate(flow);

      const hasErrors = errors && errors.length > 0;
      let response: Scratchpad | undefined;

      if (!hasErrors) {
        const t0 = performance.now();
        response = await saveFlow(flow, options);
        const t1 = performance.now();
        logger.info(`saved flow completed in ${Math.floor(t1 - t0)}ms`);
      }

      setFlowErrors(errors);
      return { scratchpad: response, errors };
    },
    [flowService, setFlowErrors, saveFlow]
  );

  const validateMultipassPath = useValidatePath({
    flowItemService,
    items: flow.items,
    scratchpadId: scratchpad?.id,
    generalErrorMessageKey: 'welding:convert_to_multipass_fatal_error',
    validateItem: (item: FlowItem) => {
      if (instanceOfFlowItemSection(item)) {
        return {
          isValid: true,
        };
      }

      // Check if the item is in a multipass or stitch section
      if (isItemInParentSection(flow.items, item)) {
        return {
          isValid: false,
          errorMessage: 'welding:convert_to_multipass_nested_error',
        };
      }

      const isSupportedMotionType =
        flowItemService.isMultipassSupportedMotionType(item);

      if (isSupportedMotionType) {
        return {
          isValid: true,
        };
      } else {
        return {
          isValid: false,
          errorMessage: 'welding:convert_to_multipass_invalid_weld_type_error',
        };
      }
    },
  });

  const validateStitchPath = useValidatePath({
    flowItemService,
    items: flow.items,
    scratchpadId: scratchpad?.id,
    generalErrorMessageKey: 'welding:convert_to_stitch_fatal_error',
    validateItem: (item: FlowItem) => {
      if (instanceOfFlowItemSection(item)) {
        return {
          isValid: true,
        };
      }

      // Check if the item is in a multipass or stitch section
      if (isItemInParentSection(flow.items, item)) {
        const errorMessage = isItemInParentSection(
          flow.items,
          item,
          BeaconWeldingData.FlowSectionType.MULTIPASS
        )
          ? 'welding:convert_to_stitch_in_multipass_error'
          : 'welding:convert_to_stitch_in_stitch_error';

        return {
          isValid: false,
          errorMessage,
        };
      }

      const isSupportedMotionType =
        flowItemService.isStitchSupportedMotionType(item);

      if (isSupportedMotionType) {
        return {
          isValid: true,
        };
      } else {
        return {
          isValid: false,
          errorMessage: 'welding:convert_to_stitch_invalid_weld_type_error',
        };
      }
    },
  });

  const quickActions: Record<string, FlowQuickAction> = useMemo(() => {
    const actions: Record<string, FlowQuickAction> = {};

    if (hasMultipassFeature) {
      actions[BeaconWeldingData.FlowItemQuickAction.CONVERT_TO_MULTIPASS] = {
        ui: {
          action: BeaconWeldingData.FlowItemQuickAction.CONVERT_TO_MULTIPASS,
          icon: multipassWeld,
          label: getLocalizedText('welding:multipass_convert'),
        },
        handler: (item, onAction) => {
          const valid = validateMultipassPath(item);

          if (!valid) {
            return;
          }

          openMultipassPath(item, onAction);
        },
        condition: (item) => {
          const canConvertToMultipass = item
            ? flowItemService.canConvertFlowItemToMultipass(item)
            : false;

          // Don't show the option if the item is in a multipass or stitch section
          if (item && isItemInParentSection(flow.items, item)) {
            return false;
          }

          // Don't allow conversion if program is running
          const programState = machine.state?.transient?.status?.programState;
          if (programState === ProgramState.PROGRAM_PLAYING) {
            return false;
          }

          return canConvertToMultipass && isEmpty(flowErrors);
        },
      };
    }

    actions[BeaconWeldingData.FlowItemQuickAction.CONVERT_TO_STITCH] = {
      ui: {
        action: BeaconWeldingData.FlowItemQuickAction.CONVERT_TO_STITCH,
        icon: stitchWeld,
        label: getLocalizedText('common:convert_to_stitch'),
      },
      handler: (item, onAction) => {
        const valid = validateStitchPath(item);

        if (!valid) {
          return;
        }

        openStitchPath(item, onAction);
      },
      condition: (item) => {
        const canConvertToStitch = item
          ? flowItemService.canConvertFlowItemToStitch(item)
          : false;

        // Don't show the option if the item is in a multipass or stitch section
        if (item && isItemInParentSection(flow.items, item)) {
          return false;
        }

        return canConvertToStitch && isEmpty(flowErrors);
      },
    };

    return actions;
  }, [
    hasMultipassFeature,
    validateMultipassPath,
    openMultipassPath,
    flowItemService,
    flow.items,
    flowErrors,
    validateStitchPath,
    openStitchPath,
    machine,
  ]);

  const flowRef = useCreateFlowRef({
    flow,
    onFlowChange: save,
    componentRegistry: {
      ApplicationSettingsForm: WeldApplicationSettingsBaseFormTabbed,
      FlowActionBarEditActions: WeldingEditActions,
      FlowItem: WeldingFlowItemComponent,
      FlowItemEdit: WeldingFlowItemEdit,
      FlowItemMultiEdit: WeldingFlowItemMultiEdit,
      TeachDetail: WeldingTeachDetail,
      FlowSections: WeldingFlowSections,
      quickActions,
    },
  });

  const isReadOnly = useMemo(() => {
    return (
      !canUpdateScratchpad ||
      !scratchpad?.active ||
      !isMachineActive ||
      !canUpdateAllBeaconWeldingFields
    );
  }, [
    canUpdateScratchpad,
    canUpdateAllBeaconWeldingFields,
    isMachineActive,
    scratchpad?.active,
  ]);

  let secondaryToolbar;

  if (flowRef) {
    secondaryToolbar = (
      <FlowActionBar
        flow={flow}
        flowRef={flowRef}
        flowValidationErrors={flowErrors}
        readOnly={isReadOnly}
        scratchpadId={scratchpad?.id}
      />
    );
  }

  const hasFlowErrors = (flowErrors?.length ?? 0) > 0;

  const presentActions = useCallback(() => {
    actionButtonRef.current?.present();
  }, []);

  const applicationSettingsModalRef = useRef<ApplicationSettingsModalRef>(null);

  const showApplicationSettings = useCallback(async () => {
    if (applicationSettingsModalRef.current) {
      return applicationSettingsModalRef.current?.showApplicationSettings();
    }

    return false;
  }, []);

  const backgroundColor =
    flow.config.stepModeEnabled === true
      ? 'var(--alt-color-header)'
      : undefined;

  return (
    <BackButtonProvider>
      <IonPage ref={pageRef}>
        <NestedPageHeader
          backgroundColor={backgroundColor}
          defaultBackRoute={builder.getMachineRoute(teamId, machineId)}
          secondaryToolbar={secondaryToolbar}
          rightButtons={
            <ActionButton
              ref={actionButtonRef}
              icon={
                <IonIcon icon={ellipsisVertical} style={{ fontSize: '24px' }} />
              }
            >
              {({ close }) => (
                <IonList lines="none">
                  <IonItem
                    button={true}
                    onClick={() => {
                      onModalChange(FlowModal.PART_DETAILS);
                    }}
                    disabled={hasFlowErrors}
                  >
                    <IonIcon slot="start" icon={informationCircleOutline} />
                    <IonLabel>
                      {getLocalizedText('welding:part_details')}
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    button={true}
                    onClick={() => {
                      onModalChange(FlowModal.WELD_SETTINGS);
                    }}
                    disabled={hasFlowErrors}
                  >
                    <IonIcon slot="start" icon={welderKeyOn} />
                    <IonLabel>
                      {getLocalizedText('welding:weld_settings')}
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    button={true}
                    onClick={() => {
                      onModalChange(FlowModal.PART_HISTORY);
                    }}
                  >
                    <IonIcon slot="start" icon={timeOutline} />
                    <IonLabel>
                      {getLocalizedText('welding:part_history')}
                    </IonLabel>
                  </IonItem>
                  <ScratchpadPlaylistOptions onAction={close} />
                </IonList>
              )}
            </ActionButton>
          }
        >
          <BeaconWeldingPageTitle title={scratchpad?.name ?? ''} />
        </NestedPageHeader>
        <IonContent>
          <FlowRefContext.Provider value={flowRef}>
            <FlowComponent
              flow={flow}
              flowRef={flowRef}
              flowValidationErrors={flowErrors}
              readOnly={isReadOnly}
              presentActions={presentActions}
              showApplicationSettings={showApplicationSettings}
            />
            {stitchPathComponent}
            {multipassPathComponent}
            <ScratchpadHistoryModal
              isOpen={activeModal === FlowModal.PART_HISTORY}
              onDidDismiss={() => setActiveModal(undefined)}
              presentingEl={pageRef.current}
            />
            <WeldPartDetailModal
              isOpen={activeModal === FlowModal.PART_DETAILS}
              onDismiss={() => setActiveModal(undefined)}
              presentingEl={pageRef.current}
              readOnly={isReadOnly}
            />
            <ApplicationSettingsModalProvider
              isOpen={activeModal === FlowModal.WELD_SETTINGS}
              onDismiss={() => setActiveModal(undefined)}
              readOnly={isReadOnly}
              presentingEl={pageRef.current}
              ref={applicationSettingsModalRef}
              defaultBucketTags={{
                welder: flowService.getEquipmentName(),
              }}
              type={ScratchpadBucketType.WELD_SETTINGS}
            />
          </FlowRefContext.Provider>
        </IonContent>
      </IonPage>
    </BackButtonProvider>
  );
};

const BeaconWeldingPageTitle = (
  props: BeaconWeldingPageTitleProps
): ReactElement => {
  const { title } = props;
  const context = useMachineContext();
  const { i18n } = useTranslation();

  const runningProgramName = useMemo(() => {
    const programName = context.machine.state?.transient?.runningProgramName;
    const mappedProgramName = BeaconWeldingData.ProgramName[programName];
    return mappedProgramName ? i18n.t(mappedProgramName) : undefined;
  }, [context.machine.state?.transient?.runningProgramName, i18n]);

  return (
    <>
      <IonTitle className="scratchpad-header">
        {title}
        <div className="subtitle header-program-status">
          <MachineProgramStatus
            machine={context.machine}
            overrideProgramName={runningProgramName}
          />
        </div>
      </IonTitle>
    </>
  );
};

/**
 * Helper function to check if an item is within a parent section
 * @param flowItems - The array of flow items
 * @param item - The item to check
 * @param sectionType - Optional section type to filter by (e.g., multipass or stitch)
 * @returns boolean indicating whether the item is within a parent section
 */
const isItemInParentSection = (
  flowItems: FlowItem[],
  item: FlowItem,
  sectionType?: BeaconWeldingData.FlowSectionType
): boolean => {
  return flowItems.some((flowItem, index) => {
    // Check if this is a parent section of the desired type (or any parent if no type specified)
    const isParentSection = instanceOfFlowItemSectionParent(flowItem);

    if (
      isParentSection &&
      (!sectionType || flowItem.attributes?.type === sectionType)
    ) {
      // Get the end index of this section
      const endIndex = flowItems.findIndex(
        (endItem) => endItem.id === flowItem.attributes?.endItemId
      );

      // If we found a valid end index
      if (endIndex !== -1) {
        // Get the index of our current item
        const itemIndex = flowItems.findIndex((i) => i.id === item.id);

        // Check if our item is between the section start and end
        return itemIndex > index && itemIndex <= endIndex;
      }
    }
    return false;
  });
};

export { BeaconWeldingPage };
